import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import React from "react";

import { IgTypography } from "@intelligems/ig-theme";
import { Stack, useTheme } from "@mui/material";

import { formatUpliftPercentage } from "../utils/mobileAnalyticsFormatters";

type ImpactCalculationColoring = "normal" | "inverted" | "neutral";

interface MobileUpliftIndicatorProps {
  uplift: {
    value?: number;
    ci_low?: number;
    ci_high?: number;
    plus_minus: number;
  };
  impactCalculationColoring: ImpactCalculationColoring;
  currency: { code: string; locale: string };
}

/**
 * Mobile-optimized uplift indicator showing directional arrow and percentage
 * Uses color logic based on impactCalculationColoring config
 */
export const MobileUpliftIndicator: React.FC<MobileUpliftIndicatorProps> = ({
  uplift,
  impactCalculationColoring,
  currency,
}) => {
  const theme = useTheme();

  // Determine change direction
  const change =
    uplift.value === undefined || uplift.value === null
      ? undefined
      : uplift.value > 0
        ? "increase"
        : uplift.value < 0
          ? "decrease"
          : undefined;

  // Calculate colors based on impactCalculationColoring
  const getIconColor = () => {
    if (!change) return theme.palette.text.primary;

    if (impactCalculationColoring === "inverted") {
      return change === "increase"
        ? theme.palette.error.main
        : theme.palette.success.main;
    }

    if (impactCalculationColoring === "neutral") {
      return theme.palette.text.primary;
    }

    // normal coloring
    return change === "increase"
      ? theme.palette.success.main
      : theme.palette.error.main;
  };

  const getTextColor = () => {
    if (!change) return "inherit";

    if (impactCalculationColoring === "inverted") {
      return change === "increase"
        ? theme.palette.text.tonal.error
        : theme.palette.text.tonal.success;
    }

    if (impactCalculationColoring === "neutral") {
      return "inherit";
    }

    // normal coloring
    return change === "increase"
      ? theme.palette.text.tonal.success
      : theme.palette.text.tonal.error;
  };

  const formattedUplift = formatUpliftPercentage(uplift.value, currency);

  if (!formattedUplift || !change) {
    return null;
  }

  const iconColor = getIconColor();
  const textColor = getTextColor();

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {change === "increase" ? (
        <ArrowUpIcon size={14} stroke={iconColor} strokeWidth={3} />
      ) : (
        <ArrowDownIcon size={14} stroke={iconColor} strokeWidth={3} />
      )}
      <IgTypography variant="caption" sx={{ color: textColor }}>
        {formattedUplift}
      </IgTypography>
    </Stack>
  );
};
